import type { ContentAnchor, ProvidersRouteParams } from 'types';
import { RouteMap } from 'constants/Routes';
import { match, P } from 'ts-pattern';
import { fullStateName } from 'pages/providers/utils';
import { ProvidersSummary } from 'types/api/documents/providers';
import { goToAnchor } from 'react-scrollable-anchor';

export const caseNull = { kind: null, state: null, city: null };
export const caseKind = { kind: P.nonNullable, state: null, city: null };
export const caseKindState = {
  kind: P.nonNullable,
  state: P.nonNullable,
  city: null,
};
export const caseKindStateCity = {
  kind: P.nonNullable,
  state: P.nonNullable,
  city: P.nonNullable,
};

/**
 * URI encodes a nullable string
 */
export const encode = (s: string | null): string =>
  s ? encodeURIComponent(s) : '';

/**
 * Builds a URL from route params
 */
export const buildURL = (
  rp: ProvidersRouteParams,
  encoded = true,
  base: string = RouteMap.PROVIDERS.base,
): string => {
  const n = encoded ? encode : (x: string | null) => (x ? x : '');
  return match(rp)
    .with(caseNull, () => base)
    .with(caseKind, () => `${base}/${n(rp.kind)}`)
    .with(caseKindState, () => `${base}/${n(rp.kind)}/${rp.state}`)
    .with(
      caseKindStateCity,
      () => `${base}/${n(rp.kind)}/${rp.state}/${n(rp.city)}`,
    )
    .otherwise(() => base);
};

export const getPageTitle = (rp: ProvidersRouteParams): string =>
  match(rp)
    .with(caseNull, () => 'Specialists A to Z')
    .with(caseKind, () => `${getSEOKind(rp.kind)} by State`)
    .with(
      caseKindState,
      () =>
        `${getSEOKind(rp.kind)}${rp.state ? ` in ${fullStateName(rp.state)}` : ''}`,
    )
    .with(
      caseKindStateCity,
      () => `${getSEOKind(rp.kind)} in ${rp.city}, ${rp.state}`,
    )
    .otherwise(() => '');

export const getPageDescription = (rp: ProvidersRouteParams): string =>
  match(rp)
    .with(
      caseNull,
      () => 'Browse our library of the best specialists to find care near you.',
    )
    .with(caseKind, () => `Find the best ${getSEOKind(rp.kind)} near you.`)
    .with(caseKindState, () => `Find the best ${getSEOKind(rp.kind)} near you.`)
    .with(
      caseKindStateCity,
      () => `Find the best ${getSEOKind(rp.kind)} in ${rp.city}, ${rp.state}.`,
    )
    .otherwise(() => '');

/**
 * Converts a kind string to an SEO friendly string
 */
// JFHCI (if 'kinds' ever change this will need to be updated)
export const getSEOKind = (kind: string | null) =>
  match(kind || '')
    .with('Acupuncture', () => 'Acupuncturists')
    .with('Addiction Medicine', () => 'Addiction Medicine Doctors')
    .with('Allergy Medicine', () => 'Allergists')
    .with('Audiology', () => 'Audiologists')
    .with('Bariatrics', () => 'Bariatric Surgeons')
    .with(
      'Behavioral Medicine',
      () => 'Psychiatrists and Psychologists (Behavioral Medicine Clinicians)',
    )
    .with('Cardiology', () => 'Cardiologists')
    .with('Cardiothoracic Medicine', () => 'Cardiothoracic Surgeons')
    .with(
      'Child and Adolescent Psychiatry',
      () => 'Child and Adolescent Psychiatrists',
    )
    .with('Chiropractor', () => 'Chiropractors')
    .with('Clinical Nurse Specialist', () => 'Clinical Nurse Specialists')
    .with('Critical Care Specialist', () => 'Critical Care Specialists')
    .with('Dentist / Dental Specialist', () => 'Dentists')
    .with('Dermatology', () => 'Dermatologists')
    .with('Endocrinology', () => 'Endocrinologists')
    .with('Endodontics', () => 'Endodontists')
    .with('Emergency Medicine Physician', () => 'Emergency Medicine Doctors')
    .with('Family Medicine', () => 'Family Medicine Doctors')
    .with('Gastroenterology', () => 'Gastroenterologists')
    .with('General Surgery', () => 'General Surgeons')
    .with('Genetics', () => 'Geneticists')
    .with('Geriatrics', () => 'Geriatric Doctors')
    .with('Hematology', () => 'Hematologists')
    .with('Hepatology', () => 'Hepatologists')
    .with('Infectious Diseases', () => 'Infectious Disease Doctors')
    .with('Integrative Health', () => 'Integrative Health Doctors')
    .with('Internal Medicine', () => 'Internists')
    .with('Medical Imaging', () => 'Medical Imaging Services')
    .with('Medical Laboratory', () => 'Medical Laboratory Services')
    .with('Medical Spa', () => 'Med Spas')
    .with('Midwifery', () => 'Midwives')
    .with('Neurology', () => 'Neurologists')
    .with('Neurosurgery', () => 'Neurosurgeons')
    .with('Obstetrics and Gynecology', () => 'OB-GYNs')
    .with('Occupational Therapist', () => 'Occupational Therapists')
    .with('Ophthalmology', () => 'Ophthalmologists')
    .with('Optometry', () => 'Optometrists')
    .with('Otolaryngology', () => 'Otolaryngologists')
    .with('Oral Surgery', () => 'Oral Surgeons')
    .with('Orthodontics', () => 'Orthodontists')
    .with('Orthopedics', () => 'Orthopedic Surgeons')
    .with('Pain Management', () => 'Pain Management Doctors')
    .with('Pathology', () => 'Pathologists')
    .with('Pediatrics', () => 'Pediatricians')
    .with('Periodontics', () => 'Periodontists')
    .with('Physical Therapy', () => 'Physical Therapists')
    .with('Plastic Surgery', () => 'Plastic Surgeons')
    .with('Podiatry', () => 'Podiatrists')
    .with('Psychiatry', () => 'Psychiatrists')
    .with('Psychology', () => 'Psychologists')
    .with('Pulmonology', () => 'Pulmonologists')
    .with('Radiology', () => 'Radiologists')
    .with('Rheumatology', () => 'Rheumatologists')
    .with('Sleep Medicine', () => 'Sleep Medicine Doctors')
    .with('Speech-Language', () => 'Speech-Language Clinicians')
    .with('Sports Medicine', () => 'Sports Medicine Doctors')
    .with('Urgent Care', () => 'Urgent Cares')
    .with('Urology', () => 'Urologists')
    .with('Vascular Medicine', () => 'Vascular Medicine Doctors')
    .otherwise(() => 'Providers');

export function removeLeadingRomanNumeralsAndLetters(str?: string) {
  // Regular expression to match Roman numerals, numbers, or letters followed by a period and space at the beginning of the string
  const pattern =
    /^(M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})|[A-Za-z0-9])\. /;

  // Replace the matched pattern with an empty string and trim any leading/trailing whitespace
  return str?.replace(pattern, '')?.trim();
}

export const getSectionAnchorsFromContent = (
  summary: ProvidersSummary,
  providersSectionLabel: string,
) => {
  const anchors: Array<{
    ariaLabel: string;
    url: string | undefined;
    handleClick?: () => void;
    id: string;
    label: string;
  }> = [];
  [summary.headerSection, ...summary.bodySections].forEach((section, i) => {
    const label = removeLeadingRomanNumeralsAndLetters(section.label);
    if (label)
      anchors.push({
        ariaLabel: `Navigate to read about ${label}`,
        url: `#${encode(label)}`,
        id: `${encode(label)}-link`,
        label: label,
        handleClick: () => goToAnchor(encode(label), true),
      });
    if (i == 0) {
      anchors.push({
        ariaLabel: `Navigate to read about ${providersSectionLabel}`,
        url: `#${encode(providersSectionLabel)}`,
        id: `${encode(providersSectionLabel)}-link`,
        label: providersSectionLabel,
      });
    }
  });
  return anchors;
};
